// Side-nav icon set, taken from the Inkfree Dashboard reference. Every icon is
// a 24-box stroke drawing (1.9 weight, round caps/joins) rather than a filled
// path, so a single `color` argument drives the whole glyph and the set reads
// as one family.
//
// They render inside `<SvgIcon>`, which sizes itself from `font-size` — so the
// inner svg is 100%/100% and scales with its wrapper instead of pinning itself
// to 24px.

interface StrokeIconProps {
  color: string;
  children: React.ReactNode;
}

const StrokeIcon = ({ color, children }: StrokeIconProps) => (
  <svg
    width="100%"
    height="100%"
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="1.9"
    strokeLinecap="round"
    strokeLinejoin="round"
    xmlns="http://www.w3.org/2000/svg"
  >
    {children}
  </svg>
);

/** Dashboard — four-panel grid. */
export const getHomeIcon = (color: string) => (
  <StrokeIcon color={color}>
    <rect x="3" y="3" width="7" height="7" rx="1.5" />
    <rect x="14" y="3" width="7" height="7" rx="1.5" />
    <rect x="14" y="14" width="7" height="7" rx="1.5" />
    <rect x="3" y="14" width="7" height="7" rx="1.5" />
  </StrokeIcon>
);

/** Templates — page with a sidebar column. */
export const getTemplatesIcon = (color: string) => (
  <StrokeIcon color={color}>
    <rect x="3" y="4" width="18" height="16" rx="2" />
    <path d="M3 9h18M9 9v11" />
  </StrokeIcon>
);

/** Web Forms — framed list of fields. */
export const getWebFormsIcon = (color: string) => (
  <StrokeIcon color={color}>
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <path d="M8 8h8M8 12h8M8 16h5" />
  </StrokeIcon>
);

/** Contacts — two people. */
export const getContactsIcon = (color: string) => (
  <StrokeIcon color={color}>
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
  </StrokeIcon>
);

/** Inbox — open tray. */
export const getInboxIcon = (color: string) => (
  <StrokeIcon color={color}>
    <path d="M22 12h-6l-2 3h-4l-2-3H2" />
    <path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
  </StrokeIcon>
);

/** Sent — document lifting out of a tray. */
export const getSentIcon = (color: string) => (
  <StrokeIcon color={color}>
    <path d="M4 14a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2" />
    <path d="M12 22V8M8 12l4-4 4 4" />
  </StrokeIcon>
);

/** Reports — axes with bars. */
/** Contract Search -- magnifier, from the design's nav (`Contract Search Standalone.html`). */
export const getContractSearchIcon = (color: string) => (
  <StrokeIcon color={color}>
    <circle cx="11" cy="11" r="7" />
    <path d="M20 20l-3.6-3.6" />
  </StrokeIcon>
);

export const getReportsIcon = (color: string) => (
  <StrokeIcon color={color}>
    <path d="M3 3v18h18" />
    <rect x="7" y="11" width="3" height="6" />
    <rect x="13" y="7" width="3" height="10" />
  </StrokeIcon>
);

/** Insights — axes with a trend line. */
export const getInsightsIcon = (color: string) => (
  <StrokeIcon color={color}>
    <path d="M3 3v18h18" />
    <path d="M7 15l4-4 3 3 5-6" />
  </StrokeIcon>
);

/** Settings — gear. */
export const getSettingsIcon = (color: string) => (
  <StrokeIcon color={color}>
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </StrokeIcon>
);

/** Home — house outline. */
export const getHomesIcon = (color: string) => (
  <StrokeIcon color={color}>
    <path d="M3 10.5 12 3l9 7.5V20a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    <path d="M9 22v-7h6v7" />
  </StrokeIcon>
);
